/**
 * CreditCounterArea — the footer-left panel (232×150) containing:
 *   - Brown backing rectangle
 *   - Backing circle (#3d3d3d, 115px) — visible only during animations
 *   - Credit counter circle (116px) with drop-in animation for decrements
 *   - Caption area: "CREDITS LEFT" during play, "FINAL SCORE" after game
 *
 * End-of-game sequence:
 *   1. FinalScoreCaption drops over CreditsLeftCaption (500ms)
 *   2. Win: credit circle flips to show final score on gold (500ms)
 *      Lose: credit circle shrinks, frowny face drops in (500ms)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import CreditCounter from './CreditCounter';
import SvgCreditsLeftCaption from '../svg/CreditsLeftCaption';
import SvgFinalScoreCaption from '../svg/FinalScoreCaption';
import SvgFrownyFace from '../svg/FrownyFace';
import NumberGlyphs from '../svg/numbers';

// Layout constants (relative to 232×150 backing rectangle)
const AREA_W = 226;
const AREA_H = 144;
const BROWN = '#514b44';
const BACKING_CIRCLE_COLOR = '#3d3d3d';
const BACKING_CIRCLE_D = 115;
const BACKING_CIRCLE_X = 97.5;
const BACKING_CIRCLE_Y = 17.5;
const COUNTER_SIZE = 116;
const COUNTER_X = 97;
const COUNTER_Y = 17;
const CAPTION_W = 86;
const CAPTION_H = 64;
const CAPTION_X = 3;
const CAPTION_Y = 43;
const GOLD = '#d1930b';
const NUMBER_SCALE = 0.675; // 0.75 * 0.9 — scaled to 90% per spec

const CAPTION_DROP_DURATION = 500;
const CIRCLE_FLIP_DURATION = 500;
const FROWNY_SHRINK_DURATION = 250;
const FROWNY_DROP_DURATION = 500;

type EndGamePhase =
  | 'idle'              // during gameplay
  | 'caption-mount'     // FinalScore caption mounted above clip, waiting for rAF
  | 'caption-dropping'  // FinalScore caption dropping into view
  | 'caption-done'      // caption in place, starting circle animation
  | 'circle-animating'  // win flip or lose shrink+drop
  | 'done';             // all animations complete

interface CreditCounterAreaProps {
  credits: number;
  gameWon: boolean | null;
  finalScore: number | null;
  /** When true, triggers the end-of-game animation sequence */
  triggerEndGame: boolean;
  /** Called when the entire end-of-game animation sequence is complete */
  onEndGameAnimComplete?: () => void;
}

export default function CreditCounterArea({
  credits,
  gameWon,
  finalScore,
  triggerEndGame,
  onEndGameAnimComplete,
}: CreditCounterAreaProps) {
  const [endPhase, setEndPhase] = useState<EndGamePhase>('idle');
  const [showBackingCircle, setShowBackingCircle] = useState(false);
  // For lose: frowny drop-in
  const [frownyPhase, setFrownyPhase] = useState<'idle' | 'shrink' | 'mount' | 'dropping' | 'done'>('idle');
  // For win: flip
  const [flipDeg, setFlipDeg] = useState(0);
  const triggeredRef = useRef(false);

  // Trigger end-of-game sequence when prop becomes true
  useEffect(() => {
    if (triggerEndGame && !triggeredRef.current) {
      triggeredRef.current = true;
      setEndPhase('caption-mount');
    }
  }, [triggerEndGame]);

  // Caption mount → dropping via double-rAF
  useEffect(() => {
    if (endPhase !== 'caption-mount') return;
    let cancelled = false;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!cancelled) setEndPhase('caption-dropping');
      });
    });
    return () => { cancelled = true; };
  }, [endPhase]);

  // Caption drop complete → start circle animation
  const handleCaptionDropEnd = useCallback(() => {
    setEndPhase('caption-done');
    setShowBackingCircle(true);
    // Start circle animation after a brief pause
    setTimeout(() => {
      setEndPhase('circle-animating');
      if (gameWon) {
        // Win: flip to back face
        setFlipDeg(180);
      } else {
        // Lose: shrink current circle, then drop frowny
        setFrownyPhase('shrink');
      }
    }, 200);
  }, [gameWon]);

  // Lose: shrink complete → mount frowny above, then drop
  useEffect(() => {
    if (frownyPhase !== 'shrink') return;
    const t = setTimeout(() => setFrownyPhase('mount'), FROWNY_SHRINK_DURATION);
    return () => clearTimeout(t);
  }, [frownyPhase]);

  useEffect(() => {
    if (frownyPhase !== 'mount') return;
    let cancelled = false;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!cancelled) setFrownyPhase('dropping');
      });
    });
    return () => { cancelled = true; };
  }, [frownyPhase]);

  const handleFrownyDropEnd = useCallback(() => {
    setFrownyPhase('done');
    setShowBackingCircle(false);
    setEndPhase('done');
    onEndGameAnimComplete?.();
  }, [onEndGameAnimComplete]);

  // Win: flip complete
  const handleFlipEnd = useCallback(() => {
    setShowBackingCircle(false);
    setEndPhase('done');
    onEndGameAnimComplete?.();
  }, [onEndGameAnimComplete]);


  const isEndGame = endPhase !== 'idle';
  const isFrownyShrunk = frownyPhase === 'shrink' || frownyPhase === 'mount' || frownyPhase === 'dropping';

  // Render the final score circle (back face of flip)
  const FinalScoreCircle = () => {
    const score = finalScore ?? 0;
    const clamped = Math.max(0, Math.min(20, score));
    const Glyph = NumberGlyphs[clamped];
    return (
      <>
        <div style={{
          position: 'absolute',
          top: 0, left: 0,
          width: COUNTER_SIZE, height: COUNTER_SIZE,
          borderRadius: '50%',
          backgroundColor: GOLD,
        }} />
        <div style={{
          position: 'absolute',
          top: 0, left: 0,
          width: COUNTER_SIZE, height: COUNTER_SIZE,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Glyph style={{ display: 'block', transform: `scale(${NUMBER_SCALE})` }} />
        </div>
      </>
    );
  };

  return (
    <div style={{
      position: 'relative',
      width: AREA_W,
      height: AREA_H,
      backgroundColor: BROWN,
      overflow: 'hidden',
    }}>
      {/* Backing circle — visible only during animations */}
      {showBackingCircle && (
        <div style={{
          position: 'absolute',
          left: BACKING_CIRCLE_X,
          top: BACKING_CIRCLE_Y,
          width: BACKING_CIRCLE_D,
          height: BACKING_CIRCLE_D,
          borderRadius: '50%',
          backgroundColor: BACKING_CIRCLE_COLOR,
        }} />
      )}

      {/* Credit counter circle area */}
      <div style={{
        position: 'absolute',
        left: COUNTER_X,
        top: COUNTER_Y,
        width: COUNTER_SIZE,
        height: COUNTER_SIZE,
        overflow: 'hidden',
      }}>
        {isEndGame && gameWon ? (
          // Win: flip container
          <div style={{
            width: COUNTER_SIZE,
            height: COUNTER_SIZE,
            perspective: 400,
          }}>
            <div style={{
              width: COUNTER_SIZE,
              height: COUNTER_SIZE,
              position: 'relative',
              transformStyle: 'preserve-3d',
              transform: `rotateY(${flipDeg}deg)`,
              transition: endPhase === 'circle-animating'
                ? `transform ${CIRCLE_FLIP_DURATION}ms ease-in-out` : 'none',
            }}
              onTransitionEnd={(e) => {
                if (e.propertyName === 'transform' && e.target === e.currentTarget) {
                  handleFlipEnd();
                }
              }}
            >
              {/* Front: current credit counter */}
              <div style={{
                position: 'absolute', inset: 0,
                backfaceVisibility: 'hidden',
              }}>
                <CreditCounter credits={credits} onAnimStart={() => setShowBackingCircle(true)} onAnimEnd={() => setShowBackingCircle(false)} />
              </div>
              {/* Back: final score on gold */}
              <div style={{
                position: 'absolute', inset: 0,
                backfaceVisibility: 'hidden',
                transform: 'rotateY(180deg)',
              }}>
                <FinalScoreCircle />
              </div>
            </div>
          </div>
        ) : isEndGame && gameWon === false ? (
          // Lose: shrink + frowny drop
          <div style={{ position: 'relative', width: COUNTER_SIZE, height: COUNTER_SIZE }}>
            {/* Current credit circle — shrinks */}
            <div style={{
              position: 'absolute', inset: 0,
              transform: `scale(${isFrownyShrunk ? 0.85 : 1})`,
              transition: frownyPhase === 'shrink' ? `transform ${FROWNY_SHRINK_DURATION}ms ease` : 'none',
              transformOrigin: 'center center',
            }}>
              <CreditCounter credits={credits} onAnimStart={() => setShowBackingCircle(true)} onAnimEnd={() => setShowBackingCircle(false)} />
            </div>
            {/* Frowny face — drops in from above */}
            {(frownyPhase === 'mount' || frownyPhase === 'dropping' || frownyPhase === 'done') && (
              <div
                style={{
                  position: 'absolute', inset: 0,
                  transform: `translateY(${frownyPhase === 'dropping' || frownyPhase === 'done' ? 0 : -COUNTER_SIZE}px)`,
                  transition: frownyPhase === 'dropping' ? `transform ${FROWNY_DROP_DURATION}ms ease` : 'none',
                  zIndex: 1,
                }}
                onTransitionEnd={(e) => {
                  if (e.propertyName === 'transform' && e.target === e.currentTarget) {
                    handleFrownyDropEnd();
                  }
                }}
              >
                <SvgFrownyFace width={COUNTER_SIZE} height={COUNTER_SIZE} />
              </div>
            )}
          </div>
        ) : (
          // Normal gameplay
          <CreditCounter credits={credits} onAnimStart={() => setShowBackingCircle(true)} onAnimEnd={() => setShowBackingCircle(false)} />
        )}
      </div>

      {/* Caption area — clip container for drop animation */}
      <div style={{
        position: 'absolute',
        left: CAPTION_X,
        top: CAPTION_Y,
        width: CAPTION_W,
        height: CAPTION_H,
        overflow: 'hidden',
      }}>
        {/* Credits Left caption — always present as base layer */}
        <div style={{
          position: 'absolute',
          top: 0, left: 0,
          width: CAPTION_W,
          height: CAPTION_H,
        }}>
          <SvgCreditsLeftCaption width={CAPTION_W} height={CAPTION_H} />
        </div>

        {/* Final Score caption — drops in from above at end of game */}
        {(endPhase === 'caption-mount' || endPhase === 'caption-dropping' || endPhase === 'caption-done'
          || endPhase === 'circle-animating' || endPhase === 'done') && (
          <div
            style={{
              position: 'absolute',
              left: 0,
              width: CAPTION_W,
              height: CAPTION_H,
              transform: `translateY(${endPhase === 'caption-mount' ? -CAPTION_H : 0}px)`,
              transition: endPhase === 'caption-dropping'
                ? `transform ${CAPTION_DROP_DURATION}ms ease` : 'none',
              zIndex: 1,
            }}
            onTransitionEnd={(e) => {
              if (e.propertyName === 'transform' && e.target === e.currentTarget) {
                handleCaptionDropEnd();
              }
            }}
          >
            <SvgFinalScoreCaption width={CAPTION_W} height={CAPTION_H} />
          </div>
        )}
      </div>
    </div>
  );
}
